import { IMessage, spLogTags } from '@eventr/common'
import {
    ColoursObj,
    drawerStore,
    Icon,
    MobileConfirmDialog,
    TDialogStore,
    Typography,
    useDialogStore,
} from '@eventr/common-storybook'
import { Box, Stack } from '@mui/material'
import { signal } from '@preact/signals-react'
import EmojiPicker, { Theme } from 'emoji-picker-react'
import { Fragment } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { IRCUserInfo } from '../../../../data/Rocket'
import i18n from '../../../../i18n/i18next'
import { useThemeContext } from '../../../../state/themeState/themeContext'
import { Utils } from '../../../../utils'
import { rocketApiSignal, rocketEditMessage } from '../chatProvider'
import { ActionSheetItem } from './actionSheetItem'
import { RocketReactionList } from './reactionList'
import { RocketReactionRow } from './reactionRow'

interface IActionSheetBodyProps {
    message: IMessage
    getDownloadUrl: (url: string) => void
    rcUserInfo: IRCUserInfo
}

export const emojiPickerState = signal<boolean>(false)

export const MessageActionSheetBody = ({
    message,
    getDownloadUrl,
    rcUserInfo,
}: IActionSheetBodyProps) => {
    const selectedColourScheme = useThemeContext().selectedColourScheme
    const { replaceDialog, clearDialog }: TDialogStore = useDialogStore(
        useShallow((s: TDialogStore) => ({
            clearDialog: s.clearDialog,
            replaceDialog: s.replaceDialog,
        }))
    )

    return (
        <Stack width={'100%'} maxHeight={'80vh'} overflow={'scroll'}>
            {emojiPickerState.value ? (
                <EmojiPicker
                    width={'100%'}
                    theme={
                        selectedColourScheme === 'dark'
                            ? Theme.DARK
                            : Theme.LIGHT
                    }
                    previewConfig={{ showPreview: false }}
                    emojiVersion="0.6" /// Higher values arnt accepted in rocket api...
                    /// Careful with this, things like 'spock' exist in
                    onEmojiClick={(emoji) => {
                        rocketApiSignal.value.reactToMessage(
                            ':' + emoji.names[0].replaceAll(' ', '_') + ':',
                            message._id
                        )
                        drawerStore().closeDrawer()
                    }}
                    autoFocusSearch={false}
                    style={{
                        border: 'none',
                    }}
                />
            ) : (
                <Fragment>
                    <RocketReactionRow message={message} />
                    {!message.file && (
                        <ActionSheetItem
                            icon={<Icon name={'Duplicate'} />}
                            label={
                                <Typography variant={'bodyRegular'}>
                                    Copy
                                </Typography>
                            }
                            onClick={() => {
                                try {
                                    message.msg &&
                                        navigator.clipboard.writeText(
                                            message.msg
                                        )
                                    drawerStore().closeDrawer()
                                } catch (e) {
                                    spLogTags("couldn't copy message", [
                                        'ERROR',
                                    ])
                                    spLogTags(e, ['ERROR'])
                                    drawerStore().closeDrawer()
                                }
                            }}
                        />
                    )}

                    {rocketApiSignal.value.chatApi ? (
                        message.u._id ===
                            rocketApiSignal.value.chatApi.userID &&
                        !message.file && (
                            <ActionSheetItem
                                icon={<Icon name={'Edit'} />}
                                label={
                                    <Typography variant={'bodyRegular'}>
                                        Edit
                                    </Typography>
                                }
                                onClick={() => {
                                    rocketEditMessage.value = message
                                    drawerStore().closeDrawer()
                                }}
                            />
                        )
                    ) : (
                        <ActionSheetItem
                            icon={
                                <Icon
                                    name={'Edit'}
                                    iconColour={ColoursObj.shades.shade3}
                                />
                            }
                            label={
                                <Typography
                                    variant={'bodyRegular'}
                                    color={ColoursObj.shades.shade3}
                                >
                                    Edit
                                </Typography>
                            }
                        />
                    )}
                    {rocketApiSignal.value.chatApi
                        ? message.file && (
                              <ActionSheetItem
                                  icon={<Icon name={'Download'} />}
                                  label={
                                      <Typography variant={'bodyRegular'}>
                                          Download
                                      </Typography>
                                  }
                                  onClick={() => {
                                      Utils.downloadAndOpenFile(
                                          getDownloadUrl(
                                              rocketApiSignal.value.chatApi
                                                  .host +
                                                  message.attachments[0]
                                                      .title_link
                                          ),
                                          message.attachments[0].title
                                      )
                                  }}
                              />
                          )
                        : message.file && (
                              <ActionSheetItem
                                  icon={
                                      <Icon
                                          name={'Download'}
                                          iconColour={ColoursObj.shades.shade3}
                                      />
                                  }
                                  label={
                                      <Typography
                                          variant={'bodyRegular'}
                                          color={ColoursObj.shades.shade3}
                                      >
                                          Download
                                      </Typography>
                                  }
                              />
                          )}
                    {rocketApiSignal.value.chatApi
                        ? message.u._id ===
                              rocketApiSignal.value.chatApi.userID && (
                              <ActionSheetItem
                                  icon={
                                      <Icon
                                          iconColour={ColoursObj.states.error}
                                          name={'Delete'}
                                      />
                                  }
                                  label={
                                      <Typography
                                          color={ColoursObj.states.error}
                                          variant={'bodyRegular'}
                                      >
                                          Delete
                                      </Typography>
                                  }
                                  onClick={() => {
                                      replaceDialog({
                                          node: (
                                              <MobileConfirmDialog
                                                  text="This item will be deleted for everyone. This cannot be undone."
                                                  header="Delete Message"
                                                  cancelText={i18n.t(
                                                      'general.cancel'
                                                  )}
                                                  confirmText={i18n.t(
                                                      'general.delete'
                                                  )}
                                                  cancelBtnStyle="borderless"
                                                  confirmBtnStyle="destructiveBorderless"
                                                  onConfirm={() => {
                                                      rocketApiSignal.value.deleteMessage(
                                                          message._id
                                                      )
                                                      clearDialog()
                                                  }}
                                                  onCancel={() => {
                                                      clearDialog()
                                                  }}
                                              />
                                          ),
                                          backdropClosable: false,
                                      })
                                      drawerStore().closeDrawer()
                                  }}
                              />
                          )
                        : message.u._id === rcUserInfo?._id && (
                              <ActionSheetItem
                                  icon={
                                      <Icon
                                          name={'Delete'}
                                          iconColour={ColoursObj.shades.shade3}
                                      />
                                  }
                                  label={
                                      <Typography
                                          variant={'bodyRegular'}
                                          color={ColoursObj.shades.shade3}
                                      >
                                          Delete
                                      </Typography>
                                  }
                              />
                          )}
                    <RocketReactionList message={message} />
                    <Box height="40px" />
                </Fragment>
            )}
        </Stack>
    )
}
